import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { PosmService } from '../posm.service';
import { PosmClientTrackingView } from '../models/posm-project.model';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';

@Component({
    selector: 'app-posm-client-tracking',
    standalone: true,
    imports: [CommonModule, TableModule, TagModule, ButtonModule, PageMetricStripComponent, PageDataPanelComponent],
    template: `
        <main class="tracking-page">
            @if (loading()) {
                <div class="tracking-shell text-center">
                    <i class="pi pi-spin pi-spinner text-3xl text-primary"></i>
                </div>
            } @else {
                @if (tracking(); as data) {
                    <div class="tracking-shell">
                        <section class="tracking-hero">
                            <div>
                                <div class="eyebrow">POSM Execution Tracking</div>
                                <h1>{{ data.projectName || 'POSM Execution' }}</h1>
                                <p>{{ data.clientName || 'Client' }} · {{ data.brandName || 'Brand not assigned' }}</p>
                                <div class="flex flex-wrap gap-2 mt-3">
                                    <p-tag [value]="data.projectNumber || '-'" severity="info"></p-tag>
                                    <p-tag [value]="data.status || 'In progress'" [severity]="statusSeverity(data.status)"></p-tag>
                                </div>
                            </div>
                        </section>

                        <app-page-metric-strip [metrics]="metrics(data)"></app-page-metric-strip>

                        <div class="grid mt-3">
                            <div class="col-12 xl:col-7">
                                <app-page-data-panel title="Store Progress" subtitle="Read-only store execution status shared by the operations team.">
                                    <p-table [value]="data.stores || []" [tableStyle]="{ 'min-width': '42rem' }" styleClass="p-datatable-sm">
                                        <ng-template pTemplate="header">
                                            <tr>
                                                <th>Store</th>
                                                <th>City</th>
                                                <th>Planned</th>
                                                <th>Status</th>
                                            </tr>
                                        </ng-template>
                                        <ng-template pTemplate="body" let-store>
                                            <tr>
                                                <td>
                                                    <div class="font-semibold text-900">{{ store.storeName || '-' }}</div>
                                                    <div class="text-sm text-600">{{ store.storeNumber || '-' }}</div>
                                                </td>
                                                <td>{{ store.city || '-' }}</td>
                                                <td>{{ store.plannedDate | date:'mediumDate' }}</td>
                                                <td><p-tag [value]="store.deliveryStatus || store.status || 'Pending'" [severity]="statusSeverity(store.deliveryStatus || store.status)"></p-tag></td>
                                            </tr>
                                        </ng-template>
                                        <ng-template pTemplate="emptymessage">
                                            <tr><td colspan="4" class="text-center text-600 py-4">No stores are available for this shared view.</td></tr>
                                        </ng-template>
                                    </p-table>
                                </app-page-data-panel>
                            </div>

                            <div class="col-12 xl:col-5">
                                <app-page-data-panel title="Task Progress" subtitle="Installation task status and evidence review state.">
                                    <p-table [value]="data.tasks || []" [tableStyle]="{ 'min-width': '32rem' }" styleClass="p-datatable-sm">
                                        <ng-template pTemplate="header">
                                            <tr>
                                                <th>Task</th>
                                                <th>Status</th>
                                                <th>Evidence</th>
                                            </tr>
                                        </ng-template>
                                        <ng-template pTemplate="body" let-task>
                                            <tr>
                                                <td>
                                                    <div class="font-semibold text-900">{{ task.storeName || '-' }}</div>
                                                    <div class="text-sm text-600">{{ task.taskType || 'Task' }}</div>
                                                </td>
                                                <td><p-tag [value]="task.status || 'Pending'" [severity]="statusSeverity(task.status)"></p-tag></td>
                                                <td>{{ task.evidenceApprovalStatus || '-' }}</td>
                                            </tr>
                                        </ng-template>
                                        <ng-template pTemplate="emptymessage">
                                            <tr><td colspan="3" class="text-center text-600 py-4">No tasks are available for this shared view.</td></tr>
                                        </ng-template>
                                    </p-table>
                                </app-page-data-panel>

                                <app-page-data-panel class="block mt-3" title="Completion Report" subtitle="Latest client-ready closeout artifact.">
                                    @if (data.latestReport) {
                                        <div class="font-semibold text-900">{{ data.latestReport.title || data.latestReport.reportNumber }}</div>
                                        <div class="text-sm text-600">{{ data.latestReport.generatedAt | date:'medium' }}</div>
                                        <div class="flex flex-wrap gap-2 mt-3">
                                            @if (data.latestReport.pdfUrl) {
                                                <p-button label="Open PDF" icon="pi pi-file-pdf" [outlined]="true" (onClick)="open(data.latestReport.pdfUrl)"></p-button>
                                            }
                                            @if (data.latestReport.pptUrl) {
                                                <p-button label="Open PPT" icon="pi pi-desktop" [outlined]="true" (onClick)="open(data.latestReport.pptUrl)"></p-button>
                                            }
                                        </div>
                                    } @else {
                                        <div class="text-sm text-600">No completion report has been shared yet.</div>
                                    }
                                </app-page-data-panel>
                            </div>
                        </div>
                    </div>
                } @else {
                    <div class="tracking-shell">
                        <section class="tracking-hero">
                            <div>
                                <div class="eyebrow">POSM Execution Tracking</div>
                                <h1>Tracking link unavailable</h1>
                                <p>{{ errorMessage() || 'This link is invalid, disabled, or expired.' }}</p>
                            </div>
                        </section>
                    </div>
                }
            }
        </main>
    `,
    styles: [`
        .tracking-page {
            min-height: 100vh;
            background: #f3f6fb;
            padding: 1rem;
        }

        .tracking-shell {
            max-width: 1180px;
            margin: 0 auto;
        }

        .tracking-hero {
            display: flex;
            align-items: center;
            min-height: 12rem;
            margin-bottom: 1rem;
            padding: 2rem;
            color: #0f172a;
            border: 1px solid #dfe7f3;
            border-radius: 8px;
            background: #ffffff;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
        }

        .eyebrow {
            color: #0f766e;
            font-size: 0.75rem;
            font-weight: 800;
            letter-spacing: 0;
            text-transform: uppercase;
        }

        h1 {
            margin: 0.35rem 0;
            color: #172033;
            font-size: 2rem;
            line-height: 1.15;
        }

        p {
            margin: 0;
            color: #64748b;
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PosmClientTrackingComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private posmService = inject(PosmService);

    public loading = signal(false);
    public tracking = signal<PosmClientTrackingView | null>(null);
    public errorMessage = signal<string | null>(null);

    ngOnInit(): void {
        const token = this.route.snapshot.paramMap.get('token');
        if (!token) {
            this.errorMessage.set('Missing tracking token.');
            return;
        }
        this.loading.set(true);
        this.posmService.getClientTracking(token).subscribe({
            next: (data) => {
                this.tracking.set(data);
                this.loading.set(false);
            },
            error: () => {
                this.errorMessage.set('This client tracking link is invalid, disabled, or expired.');
                this.loading.set(false);
            }
        });
    }

    metrics(data: PosmClientTrackingView): PageMetric[] {
        return [
            { label: 'Stores', value: data.totalStores || 0, hint: 'Target stores', tone: 'info' },
            { label: 'Tasks', value: `${data.completedTasks || 0}/${data.totalTasks || 0}`, hint: 'Completed tasks', tone: 'success' },
            { label: 'Signed DNs', value: `${data.signedDeliveryNotes || 0}/${data.totalDeliveryNotes || 0}`, hint: 'Delivery notes', tone: 'warn' },
            { label: 'Actual Cost', value: data.costVisible ? this.money(data.actualCost) : '-', hint: data.costVisible ? 'Shared by operations' : 'Not shared', tone: 'info' }
        ];
    }

    statusSeverity(status?: string): 'success' | 'secondary' | 'info' | 'warning' | 'danger' | 'contrast' {
        const normalized = (status || '').toUpperCase();
        if (['COMPLETED', 'CLOSED', 'SIGNED', 'APPROVED', 'DELIVERED'].some(value => normalized.includes(value))) return 'success';
        if (['FAILED', 'REJECTED', 'BLOCKED', 'CANCELLED'].some(value => normalized.includes(value))) return 'danger';
        if (['PENDING', 'REVIEW', 'OPEN', 'IN_PROGRESS'].some(value => normalized.includes(value))) return 'warning';
        return 'info';
    }

    open(url?: string): void {
        if (url) window.open(url, '_blank');
    }

    private money(value?: number): string {
        return new Intl.NumberFormat('en-SA', { style: 'currency', currency: 'SAR', maximumFractionDigits: 0 }).format(value || 0);
    }
}
